/**
 * AppShell.tsx
 *
 * The responsive page scaffold both apps compose: student-client's
 * Screen.tsx generalised to the WP1 geometry (docs/DESIGN.md's "Tablet and
 * desktop" section, plus the finer 1024/1280 breakdown in the WP1 brief).
 * One markup tree per variant — no `max-*`, no `Capacitor.getPlatform()`.
 *
 * `default` variant: header (optional) + nav (optional, vertical from `lg`)
 * + content column + rail (optional, `xl`/`lg` only, requires both `nav` and
 * `rail`). Below `lg` this is exactly Screen.tsx: header on top, content
 * scrolling in the middle, nav in flow at the bottom. From `lg` the nav
 * becomes a left sidebar and the header/content/rail become a row so the
 * header can span the column + rail width.
 *
 * `fullscreen` variant: the phone-width column at every breakpoint — forms,
 * onboarding, single-CTA flows stay 448px wide by design, no sidebar, no
 * rail, ever.
 */

import type { ReactNode } from 'react';
import { useMinWidth } from './useMinWidth';

interface AppShellProps {
  /** Chrome above the content — TopBar, or a back header. From `lg` this
   *  spans the full content region to the right of the nav sidebar, ending
   *  16px short of that region's own right edge (matching the content
   *  row's `lg:px-4`) — on every screen that passes `nav` (rail or not) and
   *  on `wide` screens too. Because that span is expressed relative to the
   *  region's own edges rather than a fixed pixel width, the notification
   *  bell lands the same 16px from the window's right edge on every tab,
   *  regardless of nav width, rail presence, or how wide the reading
   *  column has grown. See the inner wrapper below. */
  header?: ReactNode;
  /** Chrome below the content on phone/tablet; becomes the left sidebar from
   *  `lg`. Omit on child screens (DESIGN.md's back contract: a screen you can
   *  leave via a back control doesn't also get a nav). */
  nav?: ReactNode;
  /** The right-hand column, `lg` and `xl` only. Only rendered when `nav` is
   *  also given — the rail is a tab-root feature, not something a child
   *  screen gets. */
  rail?: ReactNode;
  /** A pinned bottom band, below the content row, at every width — unlike
   *  `nav` it never becomes a sidebar and never implies a rail. For chrome
   *  that has to stay reachable at the bottom of the column regardless of
   *  breakpoint (e.g. a composer) rather than following `nav`'s phone/desktop
   *  shape-shift. See components/Screen.tsx's transitional shim: today this
   *  is also where the in-lesson player's chat composer lands when it's
   *  passed in as `nav`, until that screen is ported to use this slot
   *  directly. */
  footer?: ReactNode;
  children: ReactNode;
  navBorder?: boolean;
  /** The header's bottom hairline — same default-on/opt-out shape as
   *  `navBorder`. League turns it off: its tab switch already ends the header. */
  headerBorder?: boolean;
  /** `footer`'s own top border — same default-on/opt-out shape as `navBorder`. */
  footerBorder?: boolean;
  variant?: 'default' | 'fullscreen';
  /** fullscreen only: bg-surface (forms) vs bg-primary (celebratory splash). */
  tone?: 'surface' | 'primary';
  /** Hide the nav wrapper entirely below `lg`, showing it only as the `lg`+
   *  sidebar. Use case: a root screen that is a child screen on phone (a
   *  back chevron, no bottom nav) but a sidebar item on desktop — e.g. the
   *  student's Profile screen. This is unrelated to NavCentre's
   *  `hideFromLg` (parent's Scan disc: a camera action with no desktop
   *  equivalent) and to NavItem's `hideBelowLg` (student's Profile tab:
   *  hidden from the bottom bar, shown in the sidebar) — those hide one nav
   *  slot; this hides the whole nav below `lg`. */
  navHiddenBelowLg?: boolean;
  /** Extra classes on the content column. */
  className?: string;
  /** The content column owns its own scroll region (default). Pass `false`
   *  when the page renders its own `flex-1 overflow-y-auto` region instead
   *  — the column then only provides `flex flex-col min-h-0` and lets the
   *  page's own scroll area do the scrolling (e.g. a page with a pinned
   *  footer below a scrolling body). */
  scroll?: boolean;
  /** fullscreen only: `safe-area-top` on the inner column (default `true`).
   *  Pass `false` when the screen wants its stage to run under the status
   *  bar/notch instead (e.g. welcome.tsx's carousel, where only an inner
   *  row — not the whole column — needs the inset). */
  safeTop?: boolean;
  /** default variant only: drop the column's `lg`/`xl` max-width and the
   *  content row's `lg:px-4` gutter, so a master-detail screen (parent's
   *  household desk) can lay out its own pane + fill-width detail instead of
   *  sitting inside the standard column (640px floor at `lg`, 672px at
   *  `xl`, now free to grow to a 1280px ceiling — see `columnWidth` below).
   *  The header does NOT follow suit — it keeps the same fluid
   *  content-region span AND the same `lg:ml-4` offset every other `nav`
   *  screen gets, so the header box is identical on every tab (see
   *  `header`'s doc comment above).
   *  Additive: default `false` leaves every other screen's geometry
   *  untouched. */
  wide?: boolean;
}

export default function AppShell({
  header,
  nav,
  rail,
  footer,
  children,
  navBorder = true,
  headerBorder = true,
  footerBorder = true,
  variant = 'default',
  tone = 'surface',
  navHiddenBelowLg = false,
  className = '',
  scroll = true,
  safeTop = true,
  wide = false,
}: AppShellProps) {
  // Called unconditionally, ahead of the fullscreen early return below —
  // Rules of Hooks: a hook can't sit after a conditional `return` since that
  // makes its call order (and thus which state slot it reads) differ between
  // renders. The fullscreen variant just doesn't use the result.
  const isLg = useMinWidth('lg');

  if (variant === 'fullscreen') {
    return (
      <div
        className={`flex flex-col h-dvh w-full ${tone === 'primary' ? 'bg-primary' : 'bg-surface'}`}
      >
        <main
          className={`flex flex-col flex-1 min-h-0 w-full max-w-md mx-auto safe-area ${safeTop ? 'safe-area-top' : ''} ${className}`}
        >
          {children}
        </main>
      </div>
    );
  }

  const showRail = Boolean(nav && rail);
  // Mounting the rail is a behaviour switch, not a layout one — it's CSS
  // `hidden lg:block` either way, but not mounting it below `lg` stops its
  // cards' own data fetches from firing on a phone that will never render
  // them. `useMinWidth` (not CSS) is exactly for this: a behaviour that has
  // to agree with a breakpoint, not a class list.
  const mountRail = showRail && isLg;

  // columnWidth only widens past the phone/tablet centred width when
  // there's a nav (or `wide` releases it outright) — a child screen (no
  // nav) stays a 672px column centred in the window at every tier, never
  // picking up the sidebar's leftover space. For the `nav` case the column
  // used to cap hard at 640/672px; that cap is gone — 640px at `lg` and
  // 672px at `xl` are now a FLOOR (never narrower than before) and the
  // column grows to fill whatever the rail and sidebar don't use, up to a
  // 1280px ceiling so an ultrawide window doesn't produce an unreadable
  // line length. This is a deliberate reversal of DESIGN.md's old "reading
  // column capped at 672px" rule — the owner decided dead space opening up
  // past `xl` should go to the reading column instead of sitting unused to
  // the right of the rail. headerInnerSpan is different on purpose: rather
  // than following the column's width, it always spans the full content
  // region to the right of the nav sidebar (mainRegion), fluidly, ending
  // 16px short of that region's own right edge — on every screen with a
  // `nav` (rail or not) and on `wide` screens too. Because it's anchored to
  // the region's own edges instead of a fixed pixel width, the header (and
  // its bell) still never changes distance from the window's right edge
  // switching tabs, matching student-client's Screen.tsx (every tab root
  // gets a DefaultRail). See fix notes on AppShellProps for the exact rule.
  const columnWidth = wide
    ? 'lg:mx-0 lg:max-w-none xl:max-w-none'
    : nav
      ? 'lg:mx-0 lg:min-w-[640px] lg:max-w-[1280px] xl:min-w-[672px]'
      : '';
  // One position for every variant that has a nav, so the header box is
  // identical everywhere and the bell can't move between tabs: `wide` used to
  // inset with `xl:pl-4` INSIDE the capped box while the others used `ml-4`
  // outside it, which put the desk's bell 16px further from the edge than
  // Practice's. Margin for all of them.
  const headerInnerPosition = nav || wide ? 'lg:mx-0 lg:ml-4' : '';
  // Fluid, not fixed: `w-auto` releases the base `w-full`, and `mr-4`
  // mirrors the `ml-4` above on the other edge, so the browser solves the
  // width as "mainRegion minus both 16px margins" — exactly the content
  // row's own available width (mainRegion minus its two `lg:px-4` insets).
  // That keeps the bell a constant 16px from mainRegion's (and therefore
  // the window's) right edge at every width, with no arbitrary pixel value
  // to keep in sync with the column's new floor/ceiling. `lg:max-w-none` is
  // load-bearing here, not decorative: the wrapper's base classes carry
  // `md:max-w-2xl` (672px), and `md:` doesn't stop applying at `lg` — without
  // this the box stays capped at 672px and `w-auto` never gets a chance to
  // act, stranding the bell far short of the region's right edge.
  const headerInnerSpan = nav || wide ? 'lg:w-auto lg:max-w-none lg:mr-4' : '';

  return (
    <div className="flex flex-col lg:flex-row h-dvh w-full bg-surface lg:safe-area-x">
      {/* Main region: header + content row (+ rail). `relative` gives an app
          an anchor for a toast/celebration positioned against this block
          rather than the whole window (e.g. centred over the column on
          desktop instead of the full viewport). */}
      <div className="relative flex-1 flex flex-col min-w-0 min-h-0">
        {header && (
          // Hairline holds at every width, not just below `lg` — from `lg`
          // there's otherwise nothing separating the header from the
          // content, and a hairline is the system's consistent choice here
          // (the nav sidebar separates itself the same way, with its own
          // `lg:border-r`). A screen can opt out with `headerBorder={false}`.
          <div
            className={`flex-shrink-0 safe-area-top ${headerBorder ? 'border-b border-border' : ''}`}
          >
            <div
              className={`mx-auto w-full max-w-md md:max-w-2xl ${headerInnerPosition} ${headerInnerSpan}`}
            >
              {header}
            </div>
          </div>
        )}

        <div
          className={`flex-1 min-h-0 flex ${wide ? '' : 'lg:pl-4 lg:pr-4 xl:pr-8'} lg:gap-6 xl:gap-8`}
        >
          {/* lg:min-w-[640px] is the handoff's 1024 arithmetic — 72 (icon
              sidebar) + 16 (left gap) + 640 (column) + 24 (gap) + 256 (rail)
              + 16 (trailing gap) = 1024, exactly the `lg` breakpoint itself,
              so the tier's minimum layout never exceeds the width that turns
              it on. The 32px trailing inset the owner asked for (rail 32px
              from the region's right edge, mirroring the column's 32px on
              the left, and matching the header's own content inset) only
              applies from `xl`, where xl:min-w-[672px]/xl:w-80/xl:gap-8
              already need more room: 72 + 16 + 672 + 32 + 320 + 32 = 1144 —
              comfortably under the `xl` breakpoint, so no equivalent
              exact-fit constraint applies there. `lg:pr-8` on its own — the
              32px gap at BOTH tiers — put the `lg` minimum at 1040, 16px past
              1024, which compressed the rail's own inset back to 16px at
              exactly the width the tier begins; `lg:pr-4 xl:pr-8` keeps `lg`
              at its exact-fit 16px and grows to 32px only once `xl` has the
              extra room for it. That arithmetic now describes the
              MINIMUM layout only: 640/672 are a floor (never narrower than
              this), not a cap — lg:max-w-[1280px] and xl:min-w-[672px] let
              the column absorb anything beyond it, a deliberate reversal of
              the old 672px column cap now that dead space was opening up
              past `xl`. No stock Tailwind container matches the floor
              values, hence the arbitrary values. headerInnerSpan no longer
              mirrors this sum with its own fixed number — it spans the
              content region fluidly instead (lg:w-auto lg:max-w-none
              lg:mr-4), landing
              the header's right edge 16px inside the region's own right
              edge at every width, whatever the column has grown to. (The
              header's own inset is unchanged by this fix — see the
              constraint in AppShellProps' `header` doc comment.)
              pb-6 only under a nav: the raised Ask/centre disc floats above
              the bar's top edge (NavCentre's -mt-4), so a trailing CTA needs
              clearance to stay tappable below it. Child screens (no nav)
              keep the flush bottom. lg drops it — the nav is a sidebar by
              then, not a bottom bar, so there's nothing to clear. */}
          <main
            className={`flex-1 min-h-0 ${scroll ? 'overflow-y-auto no-scrollbar' : 'flex flex-col'} mx-auto w-full max-w-md md:max-w-2xl ${columnWidth} ${nav && !navHiddenBelowLg ? 'pb-6 lg:pb-0' : ''} ${className}`}
          >
            {children}
          </main>

          {mountRail && (
            <div className="hidden lg:block flex-shrink-0 overflow-y-auto no-scrollbar lg:w-64 xl:w-80 lg:pt-4">
              {rail}
            </div>
          )}
        </div>

        {footer && (
          <div
            className={`flex-shrink-0 safe-area ${footerBorder ? 'border-t border-border' : ''}`}
          >
            {/* Constrained like the column itself (columnWidth + the same
                `lg:ml-4` offset the header needs) — NOT headerInnerSpan,
                which additionally spans the rail's width. The footer sits
                below the content row same as the header sits above it, so
                it needs the header's offset to line up under the sidebar,
                but it's column-only chrome, not header-style rail-spanning
                chrome. */}
            <div
              className={`mx-auto w-full max-w-md md:max-w-2xl ${columnWidth} ${headerInnerPosition}`}
            >
              {footer}
            </div>
          </div>
        )}
      </div>

      {/* Rendered after the content row so phone tab order (and DOM/visual
          order, since there's no order-last anymore) matches: content, then
          nav. `lg:order-first` alone puts it before the content row once the
          nav becomes a sidebar; the flex parent already lays it out last on
          phone from source order, no order-last needed there. */}
      {nav && (
        <div
          className={`lg:order-first flex-shrink-0 safe-area lg:safe-area-none lg:safe-area-top lg:w-[72px] xl:w-48 lg:h-full ${
            navBorder ? 'border-t border-border lg:border-t-0 lg:border-r lg:border-border' : ''
          } ${navHiddenBelowLg ? 'hidden lg:block' : ''}`}
        >
          {nav}
        </div>
      )}
    </div>
  );
}
